import React, { PureComponent } from 'react';
import {
  StyleSheet, View, Text, Image,
} from 'react-native';
import { connect } from 'react-redux';
import { SignupScreen } from 'fitbeat.gym-core';
import { translate } from 'react-i18next';
import { UserServiceClient } from 'fitbeat.api-clients';
import { defaultScale } from '../utils/scaling';
import { getNextStep, navigateToNextStep } from './actions';
import { INavigateToNextStep } from './types';
import { IAppStore } from '../HomeWorkouts';
import { GoogleAnalytics } from '../services/google-analytics';
import { TranslateProp } from '../common-types';
import { Permissions } from '../utils/Permissions';
import { Button } from '../components';
import Theme from '../theme';
import NotificationBell from '../assets/images/notification-bell.svg';
import { ButtonSize, ButtonTypes } from '../components/Button';
import NotificationImage from '../assets/images/notification-permission.png';
import { UserDataConsumer } from '../utils';

interface IProps {
  t: TranslateProp;
  navigation: any;
  user: { userProfileService: UserServiceClient };
  navigateToNextStep: (navigateToNextStep: any) => void;
  getNextStep(navigateToNextStep: INavigateToNextStep): void;
}

interface IState {
  notificationsAllowed: boolean;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: defaultScale(16),
    backgroundColor: Theme.WHITE,
  },
  headerContainer: {
    alignItems: 'center',
    marginTop: defaultScale(0),
  },
  notificationIcon: {
    marginBottom: defaultScale(20),
  },
  subHeader: {
    color: Theme.GREY_DARK_MEDIUM,
    marginBottom: defaultScale(24),
    paddingHorizontal: defaultScale(10),
    textAlign: 'center',
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: defaultScale(200),
    height: defaultScale(400),
    resizeMode: 'contain',
    transform: [{ scale: 1.5 }],
  },
  footerButtons: {
    bottom: 0,
    paddingVertical: defaultScale(30),
    width: '100%',
  },
  actionSheetContainer: {
    width: '100%',
    alignItems: 'center',
  },
  button: {
    marginTop: defaultScale(10),
    width: '100%',
  },
});

class HomeNotificationPermission extends PureComponent<IProps, IState> {
  public state = {
    notificationsAllowed: false,
  };

  private readonly googleAnalytics: GoogleAnalytics;

  public constructor(props: IProps) {
    super(props);
    this.googleAnalytics = new GoogleAnalytics();
  }

  public render() {
    const { t } = this.props;

    return (
      <View style={styles.container}>
        <View style={styles.headerContainer}>
          <View style={styles.notificationIcon}>
            <NotificationBell />
          </View>
          <Text style={Theme.textStyle.heading}>{t('getNotified')}</Text>
          <Text style={[Theme.textStyle.bodyCopyReg, styles.subHeader]}>
            {t('allowNotificationsDescription')}
          </Text>
        </View>

        <View style={styles.imageContainer}>
          <Image
            source={NotificationImage}
            style={styles.image}
          />
        </View>

        <View style={styles.footerButtons}>
          <View style={styles.actionSheetContainer}>
            <Button
              fontColor={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              containerStyle={styles.button}
              onPress={() => this.setNotificationsAllowedAndNavigateToNextStep(true)}
              title={t('allowNotifications')}
            />
            <Button
              fontColor={Theme.PRIMARY}
              buttonType={ButtonTypes.OUTLINE}
              size={ButtonSize.MEDIUM}
              containerStyle={styles.button}
              onPress={() => this.setNotificationsAllowedAndNavigateToNextStep(false)}
              title={t('noThankYou')}
            />
          </View>
        </View>
      </View>
    );
  }

  private setNotificationsAllowedAndNavigateToNextStep = async (notificationsAllowed: boolean) => {
    if (notificationsAllowed) {
      await Permissions.requestNotificationPermission();
    }

    this.props.getNextStep({
      currentScreen: SignupScreen.NotificationPermission,
      userProfile: { pushMessageSettings: { enabled: notificationsAllowed } },
      userServiceClient: this.props.user.userProfileService,
    });

    this.googleAnalytics.logEventWithParamaters('sign_up_notification_permission', { notificationsAllowed });
    this.props.navigateToNextStep(this.props.navigation);
  };
}

const mapStateToProps = (state: IAppStore) => ({
  enabledFeatures: state.auth.signup.enabledFeatures,
});

const mapDispatchToProps = {
  getNextStep,
  navigateToNextStep,
};

export const HomeNotificationPermissionScreen = translate(['auth', 'settings'])(
  connect(mapStateToProps, mapDispatchToProps)(UserDataConsumer(HomeNotificationPermission)),
);
